import { Actions, createEffect, ofType } from '@ngrx/effects';
import { TypedAction } from '@ngrx/store/src/models';
import { isObservable, Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import { generateUuid } from '@recruitee/utils';

import { DEFAULT_SCOPE_NAME } from '../entity-collection/types';
import { ActionsFromConfig, ConfigObject, EntityActionCreator } from '../types';
import { CustomEffectsConfig, EffectHandler, RecognizedAction } from './types';

/* eslint-disable max-lines-per-function */

type InferObservable<T> = T extends Observable<infer R> ? R : never;

const transferActionPayload = <
  T extends ReturnType<
    EntityActionCreator<any, { data?: any; correlationId?: string; scope?: string }>
  >,
  R extends InferObservable<ReturnType<EffectHandler<any, any, any>>>,
>(
  actionName: RecognizedAction | string,
  effectAction: T,
  payload: R,
) => {
  switch (actionName) {
    case RecognizedAction.createOneOptimistic:
    case RecognizedAction.createOnePessimistic:
      return {
        ...payload,
        correlationId: effectAction.payload.correlationId || generateUuid(),
        scope: effectAction.payload.scope || DEFAULT_SCOPE_NAME,
      };

    case RecognizedAction.updateOneOptimistic:
      return {
        ...payload,
        correlationId: effectAction.payload.correlationId || generateUuid(),
      };

    case RecognizedAction.updateOnePessimistic:
      return {
        ...(effectAction.payload.data && { data: effectAction.payload.data }),
        ...payload,
      };

    case RecognizedAction.fetch:
    case RecognizedAction.update:
      return payload;

    case RecognizedAction.fetchOne:
      const isError = 'error' in payload;
      return isError ? { ...payload, ...effectAction.payload } : payload;

    default:
      return {
        ...payload,
        scope: effectAction.payload?.scope || DEFAULT_SCOPE_NAME,
      };
  }
};

export const customActionCreator = <A extends TypedAction<any> & { payload?: P }, P = any>(
  action: A,
): EntityActionCreator<any, any> => {
  const creator = function () {
    return action;
  };

  creator.type = action.type;
  creator.actionName = action.type;
  creator.namespace = '(global)';

  return creator;
};

/**
 * This function creates a wrapper around ngrx createEffect that reduces
 * boilerplate for known actions {@link RecognizedAction}
 *
 * @example
 * private effect = createEffectFactory(actions, actions$);
 *
 * public fetchOne$ = this.effect(actions.fetchOne, () => this.httpService.fetchOne().pipe(
 *   map(data => ({ data }))
 * ))
 *
 * is equalTo
 *
 * public fetchOne$ = createEffect(() => this.actions$.pipe(
 *   ofType(actions.fetchOne),
 *   switchMap(({payload}) => this.httpService.fetchOne().pipe(
 *     map(data => actions.fetchOneSuccess({ payload: { data } })),
 *     catchError(error => actions.fetchOneFailed({ payload: { error } }))
 *   ))
 * ))
 *
 * to check how payload is transferred to Success or Failed action see {@link transferActionPayload}
 */
export const createEffectFactory =
  <EntityActions extends ActionsFromConfig<ConfigObject<any>>>(
    actions: EntityActions,
    actions$: Actions,
  ) =>
  <
    ActionCreator extends EntityActionCreator<any, any>,
    Config extends CustomEffectsConfig,
    Handler extends EffectHandler<EntityActions, ActionCreator, Config>,
  >(
    actionCreator: ActionCreator,
    effect: Handler,
    opts?: Config,
  ) => {
    const mapOperator = opts?.customMapOperator ? opts.customMapOperator : switchMap;

    return createEffect(
      () =>
        actions$.pipe(
          ofType(actionCreator.type),
          mapOperator((effectAction: ReturnType<ActionCreator>) => {
            if (actionCreator.actionName in RecognizedAction) {
              const knownEffect$ = <Observable<ReturnType<Handler>>>effect(effectAction);
              return knownEffect$.pipe(
                map(payload =>
                  actions[`${actionCreator.actionName}Success`]({
                    payload: transferActionPayload<any, ReturnType<Handler>>(
                      actionCreator.actionName,
                      effectAction,
                      payload,
                    ),
                  }),
                ),
                catchError(error =>
                  of(
                    actions[`${actionCreator.actionName}Failed`]({
                      payload: transferActionPayload<any, any>(
                        actionCreator.actionName,
                        effectAction,
                        { error },
                      ),
                    }),
                  ),
                ),
              );
            }

            const result = <ReturnType<typeof effect>>effect(effectAction);
            return isObservable(result) ? result : of(result);
          }),
        ),
      opts,
    );
  };
